<script lang="ts">
    import type {Document, LabeledIdentified} from 'typings/portaro.be.types';
    import {RecordStatusId} from 'typings/portaro.be.types';
    import type ExemplarList from '../exemplar-list';
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import {KpExemplarsPresenter} from './kp-exemplars.presenter';
    import {pipe} from 'core/utils';
    import {loc} from 'shared/utils/pipes';
    import KpGenericTabGroup from 'shared/ui-widgets/tabset/KpGenericTabGroup.svelte';
    import KpGenericTabList from 'shared/ui-widgets/tabset/KpGenericTabList.svelte';
    import KpGenericTabPanels from 'shared/ui-widgets/tabset/KpGenericTabPanels.svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import KpGenericTabPanelContent from 'shared/ui-widgets/tabset/KpGenericTabPanelContent.svelte';
    import KpGenericPanel from 'shared/ui-widgets/panel/KpGenericPanel.svelte';
    import KpGenericTabItem from 'shared/ui-widgets/tabset/KpGenericTabItem.svelte';
    import KpExemplarsList from '../kp-exemplars-list/KpExemplarsList.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';

    export let document: Document;
    export let exemplarList: ExemplarList | null;
    export let exemplarsListProperties: LabeledIdentified<string>[];
    export let exemplarTabsByBuildingsEnabled: boolean;

    $: fullWidthTabs = (exemplarList?.divisions?.length ?? 0) < 4;

    const presenter = getInjector().getByToken<KpExemplarsPresenter>(KpExemplarsPresenter.presenterName);
    const localize = getLocalization();
</script>

{#if !exemplarList}
    <KpLoadingBlock>
        {localize(/* @kp-localization detail.nacitamExemplare */ 'detail.nacitamExemplare')}
    </KpLoadingBlock>
{/if}

{#if exemplarList && !exemplarList.canCreateAny() && exemplarList.empty && !document.downloadable}
    <div class="text-muted text-center">
        <IconedContent icon="info" orientation="vertical">
            {#if document.status.id === RecordStatusId.BACKWARD_CATALOGING || document.status.id === RecordStatusId.STANDARD_DOCUMENT}
                <span>{localize(/* @kp-localization detail.DokumentJeVeZpracovani */ 'detail.DokumentJeVeZpracovani')}</span>
            {/if}

            {#if document.status.id !== RecordStatusId.BACKWARD_CATALOGING && document.status.id !== RecordStatusId.STANDARD_DOCUMENT}
                {#if exemplarList.type === 'issues'}
                    <span>{localize(/* @kp-localization detail.ZadneExemplareKDispozici */ 'detail.ZadneExemplareKDispozici')}</span>
                {:else if exemplarList.type === 'bindings'}
                    <span>{localize(/* @kp-localization detail.dokumentNemaZadneSvazaneRocniky */ 'detail.dokumentNemaZadneSvazaneRocniky')}</span>
                {:else}
                    <span>{localize(/* @kp-localization detail.ZadneExemplareKDispozici */ 'detail.ZadneExemplareKDispozici')}</span>
                {/if}
            {/if}
        </IconedContent>
    </div>
{/if}

{#if exemplarList && (!exemplarList.empty || exemplarList.canCreateAny())}
    <div class="exemplars-container">
        {#if exemplarList.type === 'exemplars'}
            <h3 class="sr-only">{localize(/* @kp-localization detail.exemplare */ 'detail.exemplare')}</h3>
        {:else if exemplarList.type === 'bindings'}
            <h3>{localize(/* @kp-localization detail.svazaneRocniky */ 'detail.svazaneRocniky')}</h3>
        {/if}

        <KpGenericTabGroup additionalClasses="kp-exemplars-tab">
            <KpGenericTabList>
                {#each exemplarList.divisions as division(division.building.id)}
                    <KpGenericTabItem tabId="tab-division-{division.building.id}"
                                      additionalClasses="{division.exemplars.length === 0 ? 'division-tab-muted' : ''}"
                                      fullWidth="{fullWidthTabs}">

                        {#if exemplarTabsByBuildingsEnabled}
                            {pipe(division.building, loc())}
                        {:else}
                            {#if exemplarList.type === 'exemplars'}
                                {localize(/* @kp-localization detail.exemplare */ 'detail.exemplare')}
                            {/if}

                            {#if exemplarList.type === 'bindings'}
                                {localize(/* @kp-localization detail.svazaneRocniky */ 'detail.svazaneRocniky')}
                            {/if}
                        {/if}

                        <span class="text-muted">{division.exemplars.length}</span>
                    </KpGenericTabItem>
                {/each}
            </KpGenericTabList>

            <KpGenericTabPanels>
                {#each exemplarList.divisions as division(division.building.id)}
                    <KpGenericTabPanelContent tabPanelId="tab-panel-division-{division.building.id}">
                        <KpGenericPanel hasBodyPadding="{false}">
                            <KpExemplarsList exemplars="{division.exemplars}"
                                             document="{document}"
                                             properties="{exemplarsListProperties}"
                                             exemplarFilter="{exemplarList.exemplarFilter}"/>

                            {#if exemplarList.canCreateWithDepartment(division.building)}
                                <div class="add-exemplar-button-container">
                                    <KpButton on:click={() => presenter.createExemplar(document, division.building)}
                                              dataQa="create-new-exemplar-button">
                                        <IconedContent icon="add">
                                            {localize(/* @kp-localization commons.pridatExemplar */ 'commons.pridatExemplar')}
                                        </IconedContent>
                                    </KpButton>
                                </div>
                            {/if}
                        </KpGenericPanel>
                    </KpGenericTabPanelContent>
                {/each}
            </KpGenericTabPanels>
        </KpGenericTabGroup>
    </div>
{/if}

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";
    @import (reference) "styles/portaro.variables.less";

    .exemplars-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: @spacing-ml;

        .add-exemplar-button-container {
            display: flex;
            justify-content: center;
            padding: @panel-body-padding;
            border-top: 1px solid @panel-default-border;
        }
    }

    :global {
        .kp-exemplars-tab {
            .kp-generic-tab-item.division-tab-muted {
                opacity: 0.5;
            }
        }
    }
</style>